import { operatorSymbolsJson } from "@corca/data";
import { BigNumber } from "bignumber.js";

import { inlineNode, numberNode, operatorNode, operatorSymbolNode, plainTextNode } from "../utils";

import type { SemanticExpr } from "../types";
import type { NodeJson } from "@corca/data";

export type UnitArgInfo = {
  display: string;
  exponent: number; // 1 for bare units, n for Pow(unit, n)
};

export class SympyParseError extends Error {}

/** The numeric value of an integer `number` leaf, or null for floats and non-number args. */
export function intToNumber(arg: SemanticExpr | SemanticExpr[]): number | null {
  if (Array.isArray(arg) || arg.type !== "number" || arg.kind !== "integer") return null;
  return Number(arg.value);
}

const MAX_DECIMAL_SIGNIFICANT_DIGITS = 15;
// Precision ceiling for the exactness probe; comfortably above MAX_DECIMAL_SIGNIFICANT_DIGITS so a
// terminating decimal that would be kept never gets misread as non-terminating.
const ExactBigNumber = BigNumber.clone({ DECIMAL_PLACES: 40, ROUNDING_MODE: BigNumber.ROUND_DOWN });

/**
 * Exact decimal expansion of p/q, or null when the expansion is non-terminating, exceeds 15
 * significant digits, or p/q are not safe integers. Exactness is verified by checking the quotient
 * multiplied back by q recovers p (a rounded, non-terminating quotient fails this).
 */
export function rationalToDecimalString(p: number, q: number): string | null {
  if (!Number.isSafeInteger(p) || !Number.isSafeInteger(q) || q <= 0) return null;

  const quotient = new ExactBigNumber(p).div(q);
  if (!quotient.times(q).isEqualTo(p)) return null;

  const out = quotient.toFixed();
  const significant = out.replace("-", "").replace(".", "").replace(/^0+/, "").length;
  if (significant > MAX_DECIMAL_SIGNIFICANT_DIGITS) return null;

  return out;
}

const divideByJson = operatorSymbolsJson.find((op) => op.key === "symbol-divide-by")!;
const plusSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-plus")!;
const minusSymbolJson = operatorSymbolsJson.find((op) => op.key === "symbol-subtraction")!;

/** Build inline children for a single unit part, e.g. "m" or "m" + power(2). */
function buildUnitPartNodes(display: string, exponent: number): NodeJson[] {
  const nodes: NodeJson[] = [plainTextNode(display)];
  if (exponent !== 1) {
    nodes.push(operatorNode("power", [inlineNode(numberNode(Math.abs(exponent).toString()))]));
  }
  return nodes;
}

/** Build a structured unit node from parsed unit parts. */
export function buildUnitNode(unitInfos: UnitArgInfo[]): NodeJson {
  const numParts = unitInfos.filter((u) => u.exponent > 0);
  const denParts = unitInfos.filter((u) => u.exponent < 0);

  const inlineChildren: NodeJson[] = [];

  if (numParts.length === 0 && denParts.length > 0) {
    inlineChildren.push(plainTextNode("1"));
  }

  for (let i = 0; i < numParts.length; i += 1) {
    if (i > 0) inlineChildren.push(plainTextNode("\u00b7"));
    inlineChildren.push(...buildUnitPartNodes(numParts[i].display, numParts[i].exponent));
  }

  if (denParts.length > 0) {
    inlineChildren.push(operatorSymbolNode(divideByJson));
    for (let i = 0; i < denParts.length; i += 1) {
      if (i > 0) inlineChildren.push(plainTextNode("\u00b7"));
      inlineChildren.push(
        ...buildUnitPartNodes(denParts[i].display, Math.abs(denParts[i].exponent)),
      );
    }
  }

  return {
    type: "unit",
    props: { placement: "postfix", unitType: "measurement" },
    children: [{ type: "inline", props: {}, children: inlineChildren }],
  };
}

export function prependPlus(...result: NodeJson[]): NodeJson[] {
  return [operatorSymbolNode(plusSymbolJson), ...result];
}

/**
 * Prepend a minus sign to the given NodeJson array.
 * SymPy works with negative numbers, but our format uses absolute values with explicit plus/minus signs.
 */
export function prependMinus(...result: NodeJson[]): NodeJson[] {
  return [operatorSymbolNode(minusSymbolJson), ...result];
}
